<template>
  <q-card
    flat
    :dark="isDarkModeGlobal"
  >
    <q-card-section
      class="q-gutter-xs"
    >
      <q-input
        :dark="isDarkModeGlobal"
        label="TUS Maximum SAR at Focus [W/kg]"
        dense
        outlined
        type="number"
        readonly
        :model-value="calculatorStore.TUS_Max_SAR_at_Focus.toFixed(2)"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            text="Specific absorption rate (SAR) is an estimate of absorbed energy, which is then relateable to temperature rise. This value is the spatial peak of the TUS SAR and lies within the focus. TUS SAR is the product of Ispta, attenuation coefficient, and fraction of attenuation that is absorption."
          />
        </template>
      </q-input>

      <q-input
        :dark="isDarkModeGlobal"
        label="TUS Geometry Factor at Focus [-]"
        filled
        dense
        type="number"
        step="0.000001"
        v-model="TUS_Geometry_Factor_at_Focus"
        hide-bottom-space
        :rules="[
          val => val >= 0 || 'Value must be >= 0',
          val => val <= 1 || 'Value must be <= 1'
        ]"
        @update:modelValue="validateAndExecuteCalculation(TUS_Geometry_Factor_at_Focus, 'pr', 'TUS_Geometry_Factor_at_Focus')"
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            text="TUS SAR changes with a relatively high spatial frequency. Before combining it with MR SAR which changes with low spatial frequency, the TUS SAR is smoothed out by convolving it with a 10 cc sphere kernel. The geometry factor is a proxy for the spatial distribution of the TUS SAR and is used for smoothing the TUS SAR. It depends on the transducer geometry and properties. The geometry factor varies between 0 and 1, with higher values indicating that the volume of the focal spot is close to the size of the kernel i.e, 10 cc. Please note that for the purpose of this calculator, the TUS maximum SAR at focus is used for temperature rise estimation, instead of the averaged SAR."
          />
        </template>
      </q-input>

      <q-input
        :dark="isDarkModeGlobal"
        label="TUS SAR at Focus Averaged over 10 gm or 10 cc [W/kg]"
        dense
        outlined
        type="number"
        readonly
        :model-value="calculatorStore.TUS_SAR_at_Focus_averaged.toFixed(2)"
        hide-bottom-space
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            text="Spatially averaged TUS SAR calculated by multiplying the TUS maximum SAR at focus with the TUS geometry factor at focus. This value represents the TUS SAR averaged over 10 g (which is approximately a volume of 10 cc)."
          />
        </template>
      </q-input>

      <q-input
        :dark="isDarkModeGlobal"
        label="MR SAR Averaged over 10 gm or 10 cc [W/kg]"
        filled
        min="0"
        dense
        type="number"
        step="0.000001"
        v-model="MR_SAR"
        :rules="[val => val >= 0 || 'Value must be > 0']"
        hide-bottom-space
        @update:modelValue="executeCalculation(MR_SAR, 'pr')"
      >
        <template #append>
          <reference-popup-proxy
            :icon="'help'"
            tooltip="Show note"
            text="SAR due to concurrent magnetic resonance (MR) imaging sequence, averaged over 10 g (which is approximately a volume of 10 cc)"
          />
        </template>
      </q-input>
    </q-card-section>
  </q-card>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref, watch} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"

const calculatorStore = useCalculatorStore()
const {
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  isDarkModeGlobal,
  brain_pressure_transmission,
  attenuation_coefficient,
  TUS_Geometry_Factor_at_Focus,
  MR_SAR,
  loadingValue
} = storeToRefs(calculatorStore)

const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)

const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 2000
let intervalTime = 100 // Time between each count

function validateAndExecuteCalculation(val: number, calcType: string, label: string) {
  if (label === 'TUS_Geometry_Factor_at_Focus') {
    if (val >= 0 && val <= 1) {
      executeCalculation(val, calcType)
    }
  }
  if (label === 'Pressure transmission [-]') {
    // val is brain_pressure_transmission_manual
    if (!!val && val > 0 && val <= 1) {

      // check linked attenuation coefficient to be valid
      if (attenuation_coefficient.value >= 0) {
        executeCalculation(val, calcType)
      }

    }
  }

  if (label === 'Attenuation coefficient [dB/cm/MHz]') {
    //  val is brain_attenuation_coeff_manual
    if (!!val && val > 0) {
      // check linked attenuation coefficient to be valid
      if (brain_pressure_transmission.value >= 0) {
        executeCalculation(val, calcType)
      }
    }
  }
}

function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
  loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>
